import { useEffect, useState } from 'react';

import BpkChevronDown from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/chevron-down';
import {
  BpkBox,
  BpkVessel,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkLink from '@skyscanner-internal/backpack-web/bpk-component-link';

import { zindexPopover } from '@skyscanner/bpk-foundations-web/tokens/base.es6';

import type { CustomNavMenuItem } from '@skyscanner-internal/landing-pages-shared-types/types-shared/CustomNavProps';

import STYLES from './CustomNav.module.scss';

type CustomNavDesktopItemProps = {
  menuItem: CustomNavMenuItem;
  onOpen: React.Dispatch<React.SetStateAction<string | null>>;
  openMenuItemId: string | null;
};

const HOVER_DELAY_OPEN = 100;
const HOVER_DELAY_CLOSE = 200;

export default function CustomNavDesktopItem({
  menuItem,
  onOpen,
  openMenuItemId,
}: CustomNavDesktopItemProps) {
  const [hoverOpen, setHoverOpen] = useState(false);

  // Adds delay to opening & closing on hover the menu as recommended by https://www.w3.org/WAI/tutorials/menus/flyout/
  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;

    if (hoverOpen) {
      timer = setTimeout(() => {
        onOpen(menuItem.id);
      }, HOVER_DELAY_OPEN);
    } else {
      timer = setTimeout(() => {
        onOpen((curr) => {
          // Prevents closing the current sub-menu if it changed during the delay
          if (curr === menuItem.id) {
            return null;
          }

          return curr;
        });
      }, HOVER_DELAY_CLOSE);
    }

    return () => clearTimeout(timer);
  }, [hoverOpen, menuItem.id, onOpen]);

  const toggleMenu = (itemId: string) => {
    if (openMenuItemId !== itemId) {
      onOpen(menuItem.id);
    } else {
      onOpen(null);
    }
  };

  if (!menuItem.subMenuItems?.length) {
    return (
      <BpkVessel key={menuItem.id} className={STYLES.MenuItem}>
        <BpkLink href={menuItem.href} alternate>
          {menuItem.label}
        </BpkLink>
      </BpkVessel>
    );
  }

  return (
    <BpkVessel
      key={menuItem.id}
      className={STYLES.MenuItem}

      onMouseOver={() => {
        setHoverOpen(true);
      }}

      onMouseOut={() => {
        setHoverOpen(false);
      }}
    >
      <BpkLink
        href={menuItem.href}
        aria-expanded={openMenuItemId === menuItem.id}
        alternate
      >
        {menuItem.label}
      </BpkLink>

      <span
        role="button"
        tabIndex={0}
        className={STYLES.Chevron}
        aria-expanded={openMenuItemId === menuItem.id}
        aria-label={`show submenu for ${menuItem.label}`}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') {
            return;
          }

          toggleMenu(menuItem.id);
        }}
        onClick={(e: any) => toggleMenu(menuItem.id)}
      >
        <BpkChevronDown />
      </span>

      <BpkBox
        data-testid="news-nav-sub-menu"
        position="absolute"
        display={openMenuItemId === menuItem.id ? 'block' : 'none'}
        zIndex={Number(zindexPopover)}
      >
        <BpkVessel className={STYLES.SubMenuInner}>
          {menuItem.subMenuItems.map((subMenuItem) => (
            <BpkVessel key={subMenuItem.id} className={STYLES.SubMenuItem}>
              <BpkLink href={subMenuItem.href} alternate>
                {subMenuItem.label}
              </BpkLink>
            </BpkVessel>
          ))}
        </BpkVessel>
      </BpkBox>
    </BpkVessel>
  );
}
